import { Outlet, NavLink, useNavigate, Navigate, useLocation, Link } from "react-router-dom";
import { useSpark } from "@/lib/store";
import { SparkSun } from "@/components/SparkSun";
import { useSunRays } from "@/lib/sunRays";

const NAV = [
  { to: "/dashboard", label: "My Journey" },
  { to: "/compass", label: "Career Compass" },
  { to: "/part-two", label: "Make My Move" },
];

export default function Layout() {
  const { userId, switchToSarah, startFresh, profile, isPilot, isDev, session, signOut } = useSpark();
  const nav = useNavigate();
  const location = useLocation();
  const isSarah = userId === "sarah";
  const { earned, fresh } = useSunRays(profile, userId);
  const isAdmin = !isPilot && !!sessionStorage.getItem("spark_admin");

  if (session === undefined) return <div className="min-h-screen" style={{ background: "hsl(var(--spark-canvas))" }} />;
  if (!isPilot && !isDev) return <Navigate to="/" replace />;
  if (isPilot && profile && !profile.pilot_welcome_seen && location.pathname !== "/welcome-note") return <Navigate to="/welcome-note" replace />;

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-40 bg-black text-white" style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
        <div className="spark-container flex items-center justify-between h-[72px] sm:h-20">
          <button
            data-testid="brand-home-link"
            onClick={() => nav("/dashboard")}
            className="flex items-center gap-4 group"
            aria-label="Spark Coaching — my journey"
          >
            <img src="/brand/lockup_white.png" alt="Spark Coaching" className="h-9 sm:h-11 w-auto" />
            <span className="hidden sm:block h-6 w-px bg-white/15" aria-hidden="true" />
            <SparkSun size={30} earned={earned} fresh={fresh} color="#C3A56B" strokeWidth={1.4} title="Your Spark Sun" testId="spark-sun-header" />
          </button>

          <nav className="hidden md:flex items-center gap-8 text-sm">
            {NAV.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}-link`}
                className={({ isActive }) =>
                  `py-1 transition-opacity border-b ${isActive ? "opacity-100 border-white" : "opacity-70 hover:opacity-100 border-transparent"}`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {isAdmin && (
              <NavLink to="/admin" data-testid="header-admin-console-link" className="text-xs px-3 py-1.5 rounded-full border border-white/30 text-white/80 hover:text-white hover:border-white transition-colors">
                Admin Console
              </NavLink>
            )}
            {isPilot ? (
              <button data-testid="sign-out-btn" onClick={async () => { await signOut(); nav("/"); }} className="spark-btn-outline-light !py-2 !px-5 text-xs">
                Sign out
              </button>
            ) : (
            <div data-testid="demo-controls" className="hidden sm:flex items-center rounded-full border border-white/20 p-1 text-xs">
              <button
                data-testid="demo-toggle-sarah"
                onClick={switchToSarah}
                className={`px-3 py-1.5 rounded-full transition-colors ${isSarah ? "bg-white text-black" : "text-white/70 hover:text-white"}`}
              >
                Sarah · demo
              </button>
              <button
                data-testid="demo-toggle-fresh"
                onClick={startFresh}
                className={`px-3 py-1.5 rounded-full transition-colors ${!isSarah ? "bg-white text-black" : "text-white/70 hover:text-white"}`}
              >
                Fresh start
              </button>
            </div>
            )}
          </div>
        </div>
      </header>

      <main className="spark-container py-10 sm:py-16">
        <Outlet />
      </main>

      <footer className="bg-black text-white/60">
        <div className="spark-container py-10 flex flex-wrap items-center justify-between gap-3 text-xs">
          <img src="/brand/lockup_white.png" alt="Spark Coaching" className="h-8 w-auto opacity-90" />
          <span>Spark is career coaching, not therapy or diagnosis.</span>
          <span>Your reflections belong to you. You decide what your coach sees. <Link to="/welcome-note?review=1" data-testid="footer-privacy-link" className="underline underline-offset-2 hover:text-white">About privacy</Link></span>
          <span>Reflections are drafted with AI, and Spark always checks its understanding with you.</span>
        </div>
      </footer>
    </div>
  );
}
